import type { GitWorkspaceConfig, FolderEntry, ReadResult, VersionInfo, WriteQueryOptions } from "../types";
import type { WorkspaceBackend } from "./WorkspaceBackend";
import { WorkspaceBackendError } from "./errors";
import { normalizeQueryFilename } from "../normalizeQueryFilename";
import { parseGitRemote } from "./gitRemote";

export interface GitProviderClient {
  validateAccess(config: GitWorkspaceConfig): Promise<void>;
  listFolder(config: GitWorkspaceConfig, folderId?: string): Promise<FolderEntry[]>;
  readQuery(config: GitWorkspaceConfig, queryId: string): Promise<ReadResult>;
  writeQuery(
    config: GitWorkspaceConfig,
    queryId: string,
    queryText: string,
    options?: WriteQueryOptions,
  ): Promise<void>;
  listVersions(config: GitWorkspaceConfig, queryId: string): Promise<VersionInfo[]>;
  readVersion(config: GitWorkspaceConfig, queryId: string, versionId: string): Promise<ReadResult>;

  deleteQuery(config: GitWorkspaceConfig, queryId: string): Promise<void>;
}

export default class GitWorkspaceBackend implements WorkspaceBackend {
  public readonly type = "git" as const;

  constructor(
    private config: GitWorkspaceConfig,
    private client?: GitProviderClient,
  ) {}

  private missingClientError(): WorkspaceBackendError {
    return new WorkspaceBackendError(
      "UNKNOWN",
      `No GitProviderClient configured for remote '${this.config.remoteUrl}'. Supported providers: GitHub, GitLab, Bitbucket Cloud (bitbucket.org), and Gitea. For self-hosted/enterprise instances, set git workspace 'provider' and/or 'apiBaseUrl'.`,
    );
  }

  async validateAccess(): Promise<void> {
    if (!this.client) throw this.missingClientError();
    return this.client.validateAccess(this.config);
  }

  async listFolder(_folderId?: string): Promise<FolderEntry[]> {
    if (!this.client) throw this.missingClientError();
    return this.client.listFolder(this.config, _folderId);
  }

  async readQuery(_queryId: string): Promise<ReadResult> {
    if (!this.client) throw this.missingClientError();
    return this.client.readQuery(this.config, _queryId);
  }

  async writeQuery(_queryId: string, _queryText: string, _options?: WriteQueryOptions): Promise<void> {
    if (!this.client) throw this.missingClientError();
    return this.client.writeQuery(this.config, _queryId, _queryText, _options);
  }

  async listVersions(_queryId: string): Promise<VersionInfo[]> {
    if (!this.client) throw this.missingClientError();
    return this.client.listVersions(this.config, _queryId);
  }

  async readVersion(_queryId: string, _versionId: string): Promise<ReadResult> {
    if (!this.client) throw this.missingClientError();
    return this.client.readVersion(this.config, _queryId, _versionId);
  }

  async deleteQuery(_queryId: string): Promise<void> {
    if (!this.client) throw this.missingClientError();
    return this.client.deleteQuery(this.config, _queryId);
  }

  async moveQuery(queryId: string, newFolderId: string): Promise<string> {
    if (!this.client) throw this.missingClientError();

    const parts = queryId.split("/").filter(Boolean);
    const filename = parts[parts.length - 1] || queryId;
    const folder = newFolderId.replace(/^\/+|\/+$/g, "");

    const newPath = folder ? `${folder}/${filename}` : filename;
    if (newPath === queryId) return queryId;

    const read = await this.client.readQuery(this.config, queryId);
    await this.client.writeQuery(this.config, newPath, read.queryText, {
      message: `Move ${filename} to ${folder || "(root)"}`,
    });
    await this.client.deleteQuery(this.config, queryId);

    return newPath;
  }

  async renameQuery(queryId: string, newLabel: string): Promise<void> {
    if (!this.client) throw this.missingClientError();

    const next = (newLabel || "").trim();
    if (!next) throw new WorkspaceBackendError("UNKNOWN", "New name is required");

    const parts = queryId.split("/").filter(Boolean);
    const oldFilename = parts.pop() || queryId;
    const folderPrefix = parts.join("/");

    const safe = next.replace(/[\\/]/g, "-");
    const newFilename = normalizeQueryFilename(safe);
    const newPath = folderPrefix ? `${folderPrefix}/${newFilename}` : newFilename;

    if (newPath === queryId) return;

    const read = await this.client.readQuery(this.config, queryId);

    await this.client.writeQuery(this.config, newPath, read.queryText, {
      message: `Rename ${oldFilename} to ${newFilename}`,
    });

    await this.client.deleteQuery(this.config, queryId);
  }

  getQueryUri(queryId: string): string | undefined {
    try {
      const { host, repoPath } = parseGitRemote(this.config.remoteUrl);
      const parts = repoPath.split("/").filter(Boolean);
      const [owner, repoName] = parts;
      if (!owner || !repoName) return undefined;

      const filePath = [this.config.rootPath, queryId].filter(Boolean).join("/");
      const branch = this.config.branch?.trim() || "HEAD";
      const provider = this.config.provider;

      const isGithub =
        provider === "github" ||
        ((!provider || provider === "auto") &&
          (host === "github.com" || (host.includes("github") && !host.includes("gitlab"))));
      if (isGithub) {
        return `https://${host}/${owner}/${repoName}/blob/${encodeURIComponent(branch)}/${filePath}`;
      }

      const isGitlab =
        provider === "gitlab" ||
        ((!provider || provider === "auto") && (host === "gitlab.com" || host.includes("gitlab")));
      if (isGitlab) {
        return `https://${host}/${repoPath}/-/blob/${encodeURIComponent(branch)}/${filePath}`;
      }

      const isBitbucket = provider === "bitbucket" || ((!provider || provider === "auto") && host === "bitbucket.org");
      if (isBitbucket) {
        return `https://bitbucket.org/${owner}/${repoName}/src/${encodeURIComponent(branch)}/${filePath}`;
      }

      // Gitea (and other self-hosted providers)
      return `https://${host}/${owner}/${repoName}/src/branch/${encodeURIComponent(branch)}/${filePath}`;
    } catch {
      return undefined;
    }
  }
}
